// BkosRatePage — the one-click rating of a BKOS buyer's first 30 days, from
// Mitchell's wrap-up email (mct_16b14ca2-a96).
//
//   /bkos/rate/:token?r=1..5   — the five buttons in the email
//
// The email link only opens this page; the page records the rating with a
// POST (mail scanners open every link, and run few scripts). A flip to a
// different rating within seconds with no tap on the page is flagged by the
// worker and left out of the average until a person confirms it here. The
// buyer can change the rating and add a note. Votes stay in the Engine.

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import TakerShell from './components/TakerShell';

const API = import.meta.env.VITE_API_BASE_URL as string;

interface PageData { first_name: string | null; rating: number | null; comment: string | null; options: Array<{ value: number; label: string }> }

const h1: CSSProperties = { fontFamily: "'Fraunces', serif", fontSize: 34, fontWeight: 900, color: '#0A0A2E', lineHeight: 1.12, margin: '0 0 12px' };
const lead: CSSProperties = { fontSize: 17, color: '#44445e', lineHeight: 1.55, margin: '0 0 22px' };
const card: CSSProperties = { background: '#fff', borderRadius: 16, padding: '22px 22px 24px', boxShadow: '0 4px 24px rgba(10,10,46,0.06)', marginBottom: 18 };
const input: CSSProperties = { width: '100%', boxSizing: 'border-box', fontSize: 16, padding: '12px 14px', borderRadius: 10, border: '1px solid #d6d3cc', background: '#fff', minHeight: 90, fontFamily: 'inherit' };
const btn: CSSProperties = { background: '#F05A28', color: '#fff', border: '2px solid #F05A28', borderRadius: 10, padding: '12px 20px', fontWeight: 700, fontSize: 16, cursor: 'pointer' };

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <TakerShell footerLabel="The Successful Bookkeeper">
      <div style={{ maxWidth: 600, margin: '0 auto', padding: '44px 16px 80px' }}>{children}</div>
    </TakerShell>
  );
}

export function BkosRatePage() {
  const { token = '' } = useParams();
  const [params] = useSearchParams();
  const fromLink = Number(params.get('r'));
  const [data, setData] = useState<PageData | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'invalid' | 'error'>('loading');
  const [note, setNote] = useState('');
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const once = useRef(false);
  const url = `${API}/api/public/bkos-rating/${encodeURIComponent(token)}`;

  const post = async (payload: Record<string, unknown>) => {
    const r = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    if (r.status === 401) { setState('invalid'); return null; }
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setErr(d?.error ?? `Something went wrong (${r.status}).`); return null; }
    setData(d as PageData);
    setState('ready');
    return d as PageData;
  };

  useEffect(() => {
    if (once.current) return;
    once.current = true;
    const webdriver = (navigator as Navigator & { webdriver?: boolean }).webdriver === true;
    if (Number.isInteger(fromLink) && fromLink >= 1 && fromLink <= 5) {
      post({ rating: fromLink, source: 'link', webdriver }).catch(() => setState('error'));
    } else {
      fetch(url).then(async (r) => {
        if (r.status === 401) { setState('invalid'); return; }
        if (!r.ok) { setState('error'); return; }
        setData((await r.json()) as PageData);
        setState('ready');
      }).catch(() => setState('error'));
    }
  }, [token]);

  const pick = async (value: number) => {
    setBusy(true); setErr('');
    try { await post({ rating: value, source: 'page' }); } catch { setErr('We could not reach our system. Please try again in a moment.'); } finally { setBusy(false); }
  };
  const sendNote = async () => {
    if (!data?.rating || !note.trim()) return;
    setBusy(true); setErr('');
    try { if (await post({ rating: data.rating, source: 'page', comment: note })) setSaved(true); } catch { setErr('We could not reach our system. Please try again in a moment.'); } finally { setBusy(false); }
  };

  if (state === 'loading') return <Shell><p style={lead}>Loading…</p></Shell>;
  if (state === 'invalid') {
    return (
      <Shell>
        <h1 style={h1}>This link has expired</h1>
        <p style={lead}>No problem. Just reply to Mitchell's email and tell him how your first 30 days went.</p>
      </Shell>
    );
  }
  if (state === 'error' || !data) {
    return (
      <Shell>
        <h1 style={h1}>Something went wrong</h1>
        <p style={lead}>Please try the link again in a moment, or reply to Mitchell's email.</p>
      </Shell>
    );
  }

  const hello = data.first_name ? `Thank you, ${data.first_name}` : 'Thank you';
  return (
    <Shell>
      <h1 style={h1}>{data.rating ? hello : 'How were your first 30 days with BKOS?'}</h1>
      <p style={lead}>
        {data.rating
          ? `You rated your first 30 days with BKOS ${data.rating} out of 5. Mitchell will see it. You can change it below.`
          : 'Pick the one that fits best. Mitchell will see it.'}
      </p>
      <div style={card}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {data.options.map((o) => {
            const on = data.rating === o.value;
            return (
              <button key={o.value} disabled={busy} onClick={() => pick(o.value)}
                style={{ flex: '1 1 90px', padding: '12px 8px', borderRadius: 10, cursor: 'pointer', fontSize: 14,
                  border: on ? '2px solid #F05A28' : '1px solid #d6d3cc', background: on ? '#FFF1EA' : '#fff', color: '#0A0A2E' }}>
                <strong style={{ fontSize: 20, display: 'block' }}>{o.value}</strong>{o.label}
              </button>
            );
          })}
        </div>
      </div>
      {data.rating && (
        <div style={card}>
          {saved || data.comment ? (
            <p style={{ ...lead, margin: 0 }}>Thanks for the note. Mitchell will read it.</p>
          ) : (
            <>
              <p style={{ ...lead, fontSize: 16, margin: '0 0 10px' }}>Anything you'd like Mitchell to know? (optional)</p>
              <textarea style={input} value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} />
              <div style={{ marginTop: 12 }}>
                <button style={btn} disabled={busy || !note.trim()} onClick={sendNote}>{busy ? 'Sending…' : 'Send to Mitchell'}</button>
              </div>
            </>
          )}
        </div>
      )}
      {err && <p style={{ color: '#b91c1c', fontSize: 14 }}>{err}</p>}
    </Shell>
  );
}
